import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import Root from './Root';
import reportWebVitals from './reportWebVitals';
import i18n from './i18n/i18n';
import { getDefaultLogger } from './components/logger/logger';
import { ClientConfig, ClientConfigContext } from './utils/clientConfigContext';
import { VeriffSessionProvider } from './utils/veriffSessionContext';
import { VerificationStatus } from './@types/VerificationStatus';
import WebIdMobileResultPage from './pages/webIdMobileResultPage/WebIdMobileResultPage';

const log = getDefaultLogger();

export interface TVSConfig {
  clientId: string;
  baseUrl?: string;
  onVerificationComplete?: (status: VerificationStatus) => void;
}

const standaloneVerificationSuccessRoot = document.getElementById('verification-success-root');
const standaloneVerificationFailureRoot = document.getElementById('verification-failure-root');

if (standaloneVerificationSuccessRoot) {
  createRoot(standaloneVerificationSuccessRoot).render(
    <I18nextProvider i18n={i18n}>
      <WebIdMobileResultPage status='success' />
    </I18nextProvider>,
  );
} else if (standaloneVerificationFailureRoot) {
  createRoot(standaloneVerificationFailureRoot).render(
    <I18nextProvider i18n={i18n}>
      <WebIdMobileResultPage status='failure' />
    </I18nextProvider>,
  );
} else
  window.TVSModal = class {
    private root;
    private readonly tvsContainer: HTMLElement | null;
    private readonly config: ClientConfig;

    constructor(containerId: string, config: TVSConfig) {
      this.tvsContainer = document.getElementById(containerId);

      this.config = {
        clientId: config.clientId,
        baseUrl: this.cleanBaseUrl(config.baseUrl || window.location.href),
      };

      if (!this.tvsContainer) {
        log.warn('Could not find root element to render TVS widget');
        return;
      }

      log.info(`Initializing TVSModal with clientId: ${this.config.clientId} and baseUrl: ${this.config.baseUrl}`);

      this.root = createRoot(this.tvsContainer);
      this.root.render(
        <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
          <I18nextProvider i18n={i18n}>
            <ClientConfigContext.Provider value={this.config}>
              <VeriffSessionProvider>
                <Root onVerificationComplete={config.onVerificationComplete ?? (() => {})} />
              </VeriffSessionProvider>
            </ClientConfigContext.Provider>
          </I18nextProvider>
        </BrowserRouter>,
      );
    }

    private cleanBaseUrl = (url: string) => {
      const urlObj = new URL(url);
      urlObj.searchParams.delete('tvs_state');
      return urlObj.toString();
    };

    public triggerVerification() {
      const url = new URL(this.config.baseUrl);
      url.searchParams.set('tvs_state', 'verification_landing_page');
      window.history.pushState({}, '', url);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  };

reportWebVitals();
